/**
 * Pure formatting helpers. Kept dependency-free so unit-style assertions
 * remain trivial. All inputs are validated; we never throw on bad data —
 * we coerce to safe fallbacks so the UI stays calm.
 */

const numberFmt = new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 2 });

/** Format a numeric scalar, collapsing tiny fractions. `null`/`NaN` → `'—'`. */
export function formatNumber(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value) || !Number.isFinite(value)) return '—';
  return numberFmt.format(value);
}

/** Format a 0..1 ratio as a localized percent string. */
export function formatPercent(ratio: number | null | undefined): string {
  if (ratio == null || Number.isNaN(ratio) || !Number.isFinite(ratio)) return '—';
  const clamped = clamp(ratio, 0, 1);
  return `${(clamped * 100).toFixed(clamped >= 0.995 || clamped <= 0.005 ? 0 : 1)}%`;
}

/** Clamp a number between min and max (inclusive). */
export function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return min;
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

/** Render an epoch-ms reset timestamp as a friendly relative or absolute string. */
export function formatResetAt(resetAtMs: number | null | undefined): string {
  if (resetAtMs == null || !Number.isFinite(resetAtMs) || resetAtMs <= 0) return '—';
  const target = new Date(resetAtMs);
  if (Number.isNaN(target.getTime())) return '—';

  const diffMs = target.getTime() - Date.now();
  if (diffMs <= 0) return '即将重置';

  const futureText = new Intl.RelativeTimeFormat('zh-CN', { numeric: 'auto' }).format(
    Math.round(diffMs / 3_600_000),
    'hour',
  );
  return `${futureText} (${formatHm(target)})`;
}

/** "HH:MM" wall-clock formatter, for displaying the actual reset time. */
function formatHm(date: Date): string {
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

/** Short human "x minutes ago" relative time (used for "last updated"). */
export function formatRelativeTime(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms <= 0) return '—';
  const diff = Date.now() - ms;
  if (diff < 0) return '刚刚';
  if (diff < 5_000) return '刚刚';
  if (diff < 60_000) return `${Math.floor(diff / 1000)}秒前`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}分钟前`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}小时前`;
  return `${Math.floor(diff / 86_400_000)}天前`;
}

/** Stable color from a string id (used for the avatar dot per provider). */
export function colorFromId(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) | 0;
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}deg 70% 55%)`;
}
